import { useCoveredPaths, pathCovered } from '../GrowthPromos/GrowthPromos';
import React from 'react';
import {
  POPULAR_SOURCE,
  POPULAR_LIMIT,
  MANUAL_OVERRIDES,
  SEASONAL,
  EVERGREEN,
} from '../../config/popularTopics';
import { nodeFor, isFeaturedPath, pathsParam } from '../../util/topicPaths';
import css from './PopularTopics.module.css';

/** Today's month-day + weekday in New York. */
export const nyToday = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).formatToParts(now);
  const get = t => parts.find(p => p.type === t)?.value;
  const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { md: `${get('month')}-${get('day')}`, ymd: `${get('year')}-${get('month')}-${get('day')}`, dow: days[get('weekday')] };
};

export const inSeason = (rule, today) => {
  const { md, dow } = today;
  const inRange = rule.from <= rule.to ? md >= rule.from && md <= rule.to : md >= rule.from || md <= rule.to;
  return inRange && (!rule.days || rule.days.includes(dow));
};

/**
 * Items for "Popular right now": manual overrides → seasonal (by weight) → evergreen fill.
 * `stats` ([{ path, bookings, searches }]) is used when POPULAR_SOURCE = 'stats'.
 */
export const popularTopicItems = ({ source = POPULAR_SOURCE, stats = [], limit = POPULAR_LIMIT, now = new Date() } = {}) => {
  const today = nyToday(now);
  const score = s => (s.bookings || 0) * 3 + (s.searches || 0);
  const ranked =
    source === 'stats' && stats.length
      ? [...stats].sort((a, b) => score(b) - score(a)).map(s => ({ path: s.path }))
      : [
          ...MANUAL_OVERRIDES.filter(o => !o.until || today.ymd <= o.until),
          ...SEASONAL.filter(r => inSeason(r, today)).sort((a, b) => (b.weight || 0) - (a.weight || 0)),
        ];
  const out = [];
  const seen = new Set();
  [...ranked, ...EVERGREEN].forEach(i => {
    if (out.length >= limit || seen.has(i.path) || !nodeFor(i.path) || !isFeaturedPath(i.path)) return;
    seen.add(i.path);
    out.push({ ...i, label: i.label || nodeFor(i.path).label });
  });
  return out;
};

const PopularTopics = ({ className, stats, activePath, linkMode }) => {
  const covered = useCoveredPaths();
  const items = popularTopicItems({ stats, limit: 30 }).filter(i => pathCovered(covered, i.path)).slice(0, POPULAR_LIMIT);
  if (!items.length) return null;
  return (
    <section className={className} data-popular-topics={items.length}>
      <h3 className={css.title}>
        <span aria-hidden="true">🔥</span> Popular right now
      </h3>
      <ul className={css.row}>
        {items.map(i => (
          <li key={i.path}>
            <a className={activePath === i.path ? `${css.item} ${css.itemOn}` : css.item} aria-current={activePath === i.path ? 'true' : undefined} href={`/s?${linkMode ? `mode=${linkMode}&` : ''}paths=${encodeURIComponent(pathsParam([i.path]))}`} data-popular-path={i.path}>
              {i.emoji ? <span aria-hidden="true">{i.emoji}</span> : null}
              <span>{i.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default PopularTopics;
